import React from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-gray-50 min-h-screen py-8 px-4 sm:px-6 lg:px-8 text-gray-800">
      <div className="max-w-4xl mx-auto bg-white p-8 sm:p-12 rounded-xl shadow-sm border border-gray-200">
        {/* Navigation & Header */}
        <div className="flex items-center justify-between border-b pb-6 mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </button>
          <span className="text-xs text-gray-400 uppercase tracking-wider">Legal Document</span>
        </div>

        <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Effective Date: October 8, 2026</p>

        <div className="space-y-8 text-sm sm:text-base leading-relaxed text-gray-700">
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Flex Logistics Management ("Company," "we," "us," or "our") respects your privacy and is committed to protecting the personal information collected through our website, mobile interfaces, logistics services, and consulting offerings (collectively, the "Services"). This Privacy Policy describes our practices regarding information collection, usage, disclosure, and user rights in compliance with applicable laws of the United States.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect several types of information to provide and improve our Services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Personal Identification Information:</strong> Full name, email address, phone number, company name, billing address, and shipping destinations provided when requesting quotes, scheduling consultations, or registering accounts.
              </li>
              <li>
                <strong>Consultation & Business Data:</strong> Operational information, logistics requirements, load details, and specific supply chain data shared during advisory consultations.
              </li>
              <li>
                <strong>Payment Information:</strong> Payment card details and billing data processed securely via third-party payment processors (e.g., Stripe). We do not store raw credit card numbers directly on our servers.
              </li>
              <li>
                <strong>Automated & Usage Data:</strong> IP addresses, browser types, device IDs, operating systems, referring URLs, and website interactions collected via cookies and analytical tags when you browse our site or interact with our digital ad campaigns.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We utilize personal data for legitimate business purposes, including to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Process, dispatch, and manage freight transport services, Bills of Lading (BOL), and logistics operations.</li>
              <li>Schedule, deliver, and document supply chain and logistics consultations.</li>
              <li>Respond to quote inquiries submitted via social media ads (e.g., Meta platforms) or website forms.</li>
              <li>Send transaction updates, billing invoices, operational notifications, and promotional communications (subject to opt-out rights).</li>
              <li>Maintain website security, fraud prevention, and regulatory compliance with U.S. Department of Transportation (DOT) and Federal Motor Carrier Safety Administration (FMCSA) standards.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. Information Sharing & Third Parties</h2>
            <p className="mb-3">
              We do not sell, rent, or trade your personal information to third parties for monetary consideration. We share information only with trusted partners necessary to provide our Services:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Service Providers:</strong> Cloud hosting (Firebase), payment processors (Stripe), email providers, and analytics platforms.</li>
              <li><strong>Logistics Partners:</strong> Vetted carriers, independent drivers, and freight dispatchers needed to fulfill shipping requests.</li>
              <li><strong>Legal Compliance:</strong> When required by federal/state law, court orders, subpoenas, or regulatory authorities.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Meta / Social Media Advertising & Analytics</h2>
            <p>
              We may utilize marketing tags (such as Meta Pixel) and conversion APIs on our website to measure ad effectiveness and deliver relevant advertising content on social media platforms. You can adjust your ad preferences through your social media account settings or opt out of targeted ad tracking via standard browser settings or industry tools like the <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Digital Advertising Alliance</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Your Privacy Rights (U.S. & State Disclosures)</h2>
            <p className="mb-3">
              Depending on your U.S. state of residence (e.g., California CCPA/CPRA, Virginia VCDPA), you may hold the following rights:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Right to request access to and copies of the personal data we hold about you.</li>
              <li>Right to request correction of inaccurate data or deletion of your personal information.</li>
              <li>Right to opt out of the processing of data for targeted behavioral advertising.</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, submit a written request to <strong>flexlogisticsmanagement1@gmail.com</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Data Security & Retention</h2>
            <p>
              We maintain technical, administrative, and physical safeguards designed to protect personal data against unauthorized access, loss, or alteration. We retain personal information for as long as reasonably required to fulfill active contracts, meet regulatory reporting requirements, or resolve legal disputes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Contact Information</h2>
            <p>
              If you have questions regarding this Privacy Policy or our legal compliance, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-900">Flex Logistics Management</p>
              <p className="text-gray-600">Email: flexlogisticsmanagement1@gmail.com</p>
              <p className="text-gray-600">Location: United States</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;